import React, { useEffect, useState } from 'react';
import { Box, Typography, Stack, alpha, Avatar, Tooltip, Paper, Divider } from '@mui/material';
import {
  LocationOnRounded,
  SettingsSuggestRounded,
  FactCheckRounded,
  MonitorWeightRounded,
} from '@mui/icons-material';
import { openNotification } from '../../../../helpers/notification';
import LoaderSkeleton from '../../../common/LoaderSkeleton';
import { Empty } from 'antd';
import {
  getEquipments,
  getFacilitiyByIdV2,
  getMeasurements,
  getStandardSamplePoints,
} from '../../../../helpers/api';
import MUIDataTable from 'mui-datatables';

export default function FacilitySamplePoints({ facilityId }) {
  const [samplePointList, setSamplePointList] = useState([]);
  const [samplePointFetching, setSamplePointLoading] = useState(false);
  const [equipmentList, setEquipmentList] = useState([]);
  const [measurementList, setMeasurementList] = useState([]);
  const [standardSamplePointList, setStandardSamplePointList] = useState([]);

  useEffect(() => {
    if (facilityId) {
      handleFetchFacilityItem();
      fetchEquipment();
      fetchMeasurement();
      fetchStandardSamplePoint();
    }
  }, [facilityId]);

  const handleFetchFacilityItem = async () => {
    setSamplePointLoading(true);
    try {
      const res = await getFacilitiyByIdV2(facilityId);
      const { data, status } = res?.result || {};
      if (status?.code === '00') {
        setSamplePointList(data?.samplePoints || []);
      } else {
        openNotification({ type: 'error', message: status?.desc || 'Error fetching details' });
      }
    } catch (error) {
      openNotification({ type: 'error', message: 'Unable to fetch facility sample points.' });
    } finally {
      setSamplePointLoading(false);
    }
  };

  const fetchStandardSamplePoint = async () => {
    try {
      const res = await getStandardSamplePoints();
      if (res?.result?.status?.code === '00') setStandardSamplePointList(res.result.data || []);
    } catch (e) {
      console.error('Standard Sample Point Fetch Error:', e);
    }
  };

  const fetchEquipment = async () => {
    try {
      const res = await getEquipments();
      if (res?.result?.status?.code === '00') setEquipmentList(res.result.data || []);
    } catch (e) {
      console.error('Equipment Fetch Error:', e);
    }
  };

  const fetchMeasurement = async () => {
    try {
      const res = await getMeasurements();
      // Added safety check for nested status code
      if (res?.result?.status?.code === '00') {
        setMeasurementList(res.result.data || []);
      }
    } catch (error) {
      // Catching the error prevents the runtime crash shown in your screenshot
      console.error('Measurement Fetch Error:', error);
    }
  };

  const compactCell = { style: { padding: '8px 12px' } };

  const renderContent = () => {
    const columns = [
      {
        name: 'icon',
        label: 'ID',
        options: {
          setCellProps: () => compactCell,
          customHeadLabelRender: (a) => (
            <Typography variant="caption" fontWeight={700} color="textSecondary">
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => (
            <Avatar
              src={samplePointList[index]?.icon}
              variant="rounded"
              sx={{
                width: 32,
                height: 32,
                bgcolor: alpha('#1976d2', 0.1),
                border: '1px solid',
                borderColor: alpha('#1976d2', 0.2),
                p: 0.5,
              }}
            >
              <LocationOnRounded sx={{ fontSize: 18, color: 'primary.main' }} />
            </Avatar>
          ),
        },
      },
      {
        name: 'samplePointName',
        label: 'Name',
        options: {
          setCellProps: () => compactCell,
          customHeadLabelRender: (a) => (
            <Typography variant="caption" fontWeight={700} color="textSecondary">
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => {
            const record = samplePointList[index];
            const name = record?.standardSamplePointId
              ? standardSamplePointList?.find(
                  (x) => x.standardSamplePointId === record?.standardSamplePointId
                )?.standardSamplePointName
              : record?.samplePointName;
            return (
              <Typography variant="body2" fontWeight={700}>
                {name || 'Unnamed Point'}
              </Typography>
            );
          },
        },
      },
      {
        name: 'measurementId',
        label: 'Measurement',
        options: {
          setCellProps: () => compactCell,
          customHeadLabelRender: (a) => (
            <Typography variant="caption" fontWeight={700} color="textSecondary">
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => {
            const m = measurementList?.find(
              (x) => x?.measurementId === samplePointList[index]?.measurementId
            );
            return (
              <Stack direction="row" spacing={1} alignItems="center">
                <SettingsSuggestRounded sx={{ fontSize: 16, color: 'text.disabled' }} />
                <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                  {m?.measurementName || 'N/A'}
                </Typography>
              </Stack>
            );
          },
        },
      },
      // {
      //   name: 'checkCount',
      //   label: 'Runs',
      //   options: {
      //     setCellProps: () => ({ style: { padding: '8px 12px', textAlign: 'center' } }),
      //     customHeadLabelRender: (a) => (
      //       <Typography variant="caption" fontWeight={700} color="textSecondary" align="center">
      //         {a.label}
      //       </Typography>
      //     ),
      //     customBodyRenderLite: (index) => (
      //       <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
      //         <FactCheckRounded sx={{ fontSize: 14, color: 'success.main' }} />
      //         <Typography variant="body2" fontWeight={700}>
      //           {samplePointList[index]?.checkCount}
      //         </Typography>
      //       </Box>
      //     ),
      //   },
      // },
      {
        name: 'equipmentId',
        label: 'Equipment',
        options: {
          setCellProps: () => compactCell,
          customHeadLabelRender: (a) => (
            <Typography variant="caption" fontWeight={700} color="textSecondary">
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => {
            const eq = equipmentList?.find(
              (x) => x?.equipmentId === samplePointList[index]?.equipmentId
            );
            return eq ? (
              <Tooltip title="Calibrated Equipment">
                <Stack direction="row" spacing={1} alignItems="center">
                  <MonitorWeightRounded sx={{ fontSize: 16, color: 'secondary.main' }} />
                  <Typography variant="body2" fontWeight={500}>
                    {eq?.equipmentName}
                  </Typography>
                </Stack>
              </Tooltip>
            ) : (
              <Typography variant="caption" color="text.disabled">
                None
              </Typography>
            );
          },
        },
      },
      {
        name: 'coordinates',
        label: 'Coordinates',
        options: {
          setCellProps: () => compactCell,
          customHeadLabelRender: (a) => (
            <Typography variant="caption" fontWeight={700} color="textSecondary">
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => {
            const coord = samplePointList[index]?.coordinates;
            return (
              <Stack direction="column" spacing={0.2}>
                <Box display="flex" gap={1}>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{ color: 'primary.main', width: 25 }}
                  >
                    LAT
                  </Typography>
                  <Typography variant="caption" fontWeight={600}>
                    {coord?.latitude || '0.00'}
                  </Typography>
                </Box>
                <Box display="flex" gap={1}>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{ color: 'secondary.main', width: 25 }}
                  >
                    LNG
                  </Typography>
                  <Typography variant="caption" fontWeight={600}>
                    {coord?.longitude || '0.00'}
                  </Typography>
                </Box>
              </Stack>
            );
          },
        },
      },
    ];

    return (
      <Paper
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '12px',
          overflow: 'hidden',
        }}
      >
        <MUIDataTable
          data={samplePointList}
          columns={columns}
          options={{
            elevation: 0,
            search: false,
            filter: false,
            download: false,
            print: false,
            viewColumns: false,
            selectableRows: 'none',
            responsive: 'standard',
            pagination: false,
            fixedHeader: true,
            tableBodyHeight: '65vh',
            setRowProps: (ev, dataIndex) => ({
              style: { backgroundColor: dataIndex % 2 === 0 ? '#ffffff' : alpha('#f5f7ff', 0.5) },
            }),
            setTableProps: () => ({ size: 'small' }),
          }}
        />
      </Paper>
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
        {/* <Avatar sx={{ bgcolor: alpha('#00ab55', 0.1), color: '#00ab55', width: 32, height: 32 }}>
          <FactCheckRounded sx={{ fontSize: 18 }} />
        </Avatar> */}
        <Box>
          <Typography variant="h6" fontWeight={700} color="text.primary" sx={{ lineHeight: 1.2 }}>
            Sample Points
          </Typography>
          <Typography variant="body2" color="textSecondary">
            List of registered emission monitoring points
          </Typography>
        </Box>
      </Box>

      {samplePointFetching ? (
        <Box sx={{ mt: 2 }}>
          <LoaderSkeleton height={40} />
          <Divider sx={{ my: 2 }} />
          <LoaderSkeleton height={200} />
        </Box>
      ) : samplePointList?.length < 1 ? (
        <Box
          sx={{ py: 10, textAlign: 'center', bgcolor: alpha('#f4f6f8', 0.5), borderRadius: '12px' }}
        >
          <Empty description="No sample points found for this facility" />
        </Box>
      ) : (
        renderContent()
      )}
    </Box>
  );
}
